import React from 'react'
import { useTable, useSortBy, useGlobalFilter, useAsyncDebounce } from 'react-table'

/**
 * Case-insensitive "contains" match across every column of a row.
 * Matches on the accessor values (not the rendered cell), and skips
 * empty values so a search like "und" can't match `undefined`.
 */
function containsText(rows, columnIds, filterValue) {
  const search = String(filterValue).toLowerCase().trim()
  if (!search) return rows
  return rows.filter(row =>
    columnIds.some(id => {
      const value = row.values[id]
      if (value === null || value === undefined || value === '') return false
      return String(value).toLowerCase().includes(search)
    })
  )
}

function TableSearch({ totalCount, filteredCount, globalFilter, setGlobalFilter, placeholder }) {
  const [value, setValue] = React.useState(globalFilter || '')

  const applyFilter = useAsyncDebounce(v => {
    setGlobalFilter(v || undefined)
  }, 200)

  const onChange = e => {
    setValue(e.target.value)
    applyFilter(e.target.value)
  }

  const onClear = () => {
    setValue('')
    setGlobalFilter(undefined)
  }

  return (
    <div className="tableSearch">
      {/* <span className="tableSearch__count">
        {globalFilter ? `${filteredCount} of ${totalCount}` : `${totalCount} record${totalCount === 1 ? '' : 's'}`}
      </span> */}
      <input
        className="form-control"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={onChange}
      />
      {/* kept in the flow when empty so the search box doesn't shift as you type */}
      <button
        type="button"
        className={`btn btn-outline-secondary${value ? '' : ' is-hidden'}`}
        tabIndex={value ? 0 : -1}
        aria-hidden={value ? undefined : true}
        onClick={onClear}
      >
        Clear
      </button>
    </div>
  )
}

export function Table({
  columns,
  data,
  searchable = false,
  searchPlaceholder = 'Search...',
  noResultsText = 'No matching records found.',
  toolbar = null
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { globalFilter },
  } = useTable(
    {
      columns,
      data,
      globalFilter: containsText,
    },
    // useGlobalFilter has to be registered before useSortBy
    useGlobalFilter,
    useSortBy
  )

  return (
    <>
      {(searchable || toolbar) && (
        <div className="tableToolbar">
          <div className="tableToolbar__left">{toolbar}</div>
          {searchable && (
            <TableSearch
              totalCount={preGlobalFilteredRows.length}
              filteredCount={rows.length}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeholder={searchPlaceholder}
            />
          )}
        </div>
      )}
      <table className="tableList" {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length === 0 && searchable ? (
            <tr>
              <td className="tableList__empty" colSpan={columns.length}>{noResultsText}</td>
            </tr>
          ) : (
            rows.map(
              (row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      )
                    })}
                  </tr>
                )
              }
            )
          )}
        </tbody>
      </table>
    </>
  )
}
